'use client';

import { AlertOwnerState, Zoom } from '@mui/material';
import { createTheme, Theme } from '@mui/material/styles';
import localFont from 'next/font/local';
import { COLORS, MOTION, RADIUS, SHADOWS } from './constants';

const getPath = (source: object, path: string): unknown =>
  path.split('.').reduce<unknown>((value, key) => (value as Record<string, unknown> | undefined)?.[key], source);

/** Colors the Typography component already styles through its own variants. */
const isTypographyPaletteColor = (palette: Theme['palette'], color: string) =>
  (color.startsWith('text') &&
    typeof getPath(palette.text, color.charAt(4).toLowerCase() + color.slice(5)) === 'string') ||
  typeof (palette as unknown as Record<string, { main?: unknown } | undefined>)[color]?.main === 'string';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    inverted: true;
    link: true;
  }
}
declare module '@mui/material/styles' {
  interface TypographyVariants {
    placeholder: React.CSSProperties;
    body3: React.CSSProperties;
    helperText: React.CSSProperties;
    h1withIcon: React.CSSProperties;
    h2withIcon: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    placeholder?: React.CSSProperties;
    body3?: React.CSSProperties;
    helperText?: React.CSSProperties;
    h1withIcon: React.CSSProperties;
    h2withIcon: React.CSSProperties;
  }
  interface TableRowVariantsOptions {
    mobileFlex?: React.CSSProperties;
    hideWrapBorders?: React.CSSProperties;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    placeholder: true;
    body3: true;
    helperText: true;
    h1withIcon: true;
    h2withIcon: true;
  }
}

declare module '@mui/material/FormControlLabel' {
  interface FormControlLabelProps {
    variant?: string;
  }
}
declare module '@mui/material/TableRow' {
  interface TableRowOwnProps {
    variant?: string;
  }
}

// Secondary brand typeface: paragraphs and body text. Cocogoose Pro stays for titles, highlights and CTAs
const onest = localFont({
  src: [
    {
      path: './fonts/Onest-Light.woff2',
      weight: '300',
      style: 'normal',
    },
    {
      path: './fonts/Onest-Regular.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './fonts/Onest-Medium.woff2',
      weight: '500',
      style: 'normal',
    },
    {
      path: './fonts/Onest-SemiBold.woff2',
      weight: '600',
      style: 'normal',
    },
    {
      path: './fonts/Onest-Bold.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
  display: 'swap',
});
const cocogoose = localFont({
  src: [
    {
      path: './fonts/Cocogoose-Pro-Thin.woff2',
      weight: '200',
      style: 'normal',
    },
    {
      path: './fonts/Cocogoose-Pro-Light.woff2',
      weight: '300',
      style: 'normal',
    },
    {
      path: './fonts/Cocogoose-Pro-Semilight.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './fonts/Cocogoose-Pro-Regular.woff2',
      weight: '500',
      style: 'normal',
    },
    {
      path: './fonts/Cocogoose-Pro-Bold.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
  display: 'swap',
});
// Fades siblings in while sliding them up, 60ms apart and capped at 8 steps, like customer-mobile's FadeInUp lists
// Shown only for keyboard focus (never on click or tap), like BackArrow's ring
const KEYBOARD_FOCUS_RING = { outline: `2px solid ${COLORS.brand.main}`, outlineOffset: 2 };

const STAGGERED_FADE_IN_UP = {
  animation: `directoFadeInUp ${MOTION.medium}ms ${MOTION.easeOut} backwards`,
  ...Object.fromEntries(
    Array.from({ length: MOTION.maxStaggerIndex }, (_, index) => [
      `&:nth-of-type(${index + 2})`,
      { animationDelay: `${(index + 1) * MOTION.stagger}ms` },
    ]),
  ),
  '&:nth-of-type(n + 10)': { animationDelay: `${MOTION.maxStaggerIndex * MOTION.stagger}ms` },
};

/**
 * Default MUI theme to use in all proyects
 */

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: COLORS.brand.main,
      light: COLORS.brand.light,
    },
    // The other brand color, for the small accent share
    secondary: {
      main: COLORS.brand.accent,
    },
    error: {
      main: COLORS.state.error,
    },
    warning: {
      main: COLORS.state.warning,
    },
    text: {
      primary: COLORS.text.main,
      secondary: COLORS.text.light,
      disabled: COLORS.state.disabledText,
    },
    background: { paper: COLORS.surface.raised, default: COLORS.background[3] },
    divider: COLORS.border.brand,
  },
  typography: {
    fontFamily: cocogoose.style.fontFamily,
    // Type scale mirrors customer-mobile: title1 (h1), title2 (h2), subtitle1, subtitle2 and body (body1)
    h1: {
      fontSize: '1.5rem',
      lineHeight: '1.875rem',
      letterSpacing: '-0.025em',
      fontWeight: 700,
      color: COLORS.text.main,
    },
    h2: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      letterSpacing: '-0.025em',
      fontWeight: 500,
      color: COLORS.primary.blue.main,
    },
    subtitle1: {
      color: COLORS.text.brand,
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      fontWeight: 500,
    },
    subtitle2: {
      color: COLORS.text.light,
      fontSize: '.875rem',
      lineHeight: '1.125rem',
      fontFamily: onest.style.fontFamily,
      fontWeight: 400,
    },
    body1: {
      fontFamily: onest.style.fontFamily,
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },
    body2: {
      fontFamily: onest.style.fontFamily,
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
    },
    button: {
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      fontWeight: 500,
    },
    helperText: {
      fontFamily: onest.style.fontFamily,
      fontSize: '0.75rem',
      lineHeight: '1rem',
      color: COLORS.text.light3,
    },
    placeholder: {
      fontSize: '1rem',
      color: COLORS.text.placeholder,
    },
    body3: {
      fontFamily: onest.style.fontFamily,
      fontSize: '.875rem',
      color: COLORS.text.light3,
    },
    h1withIcon: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      // 4px crowded wide icons (payments, sell) against the heavy title
      gap: '8px',
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: cocogoose.style.fontFamily,
      lineHeight: '1.875rem',
      letterSpacing: '-0.025em',
      color: COLORS.text.main,
    },
    h2withIcon: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '6px',
      fontSize: '1rem',
      fontWeight: 500,
      fontFamily: cocogoose.style.fontFamily,
      lineHeight: '1.25rem',
      letterSpacing: '-0.025em',
      color: COLORS.primary.blue.main,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          // MUI defaults for built-in variants
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'h6',
          subtitle2: 'h6',
          body1: 'p',
          body2: 'p',
          inherit: 'p',
          // Custom variants (unmapped ones would fall back to span)
          h1withIcon: 'h1',
          h2withIcon: 'h2',
          // Kept inline: used inside table cells and other Typography elements
          body3: 'span',
          placeholder: 'span',
          helperText: 'span',
        },
      },
      styleOverrides: {
        // MUI 9 dropped system props: Typography only styles palette keys (`primary`, `textSecondary`...) and ignores
        // any other `color`. Keep raw values (`COLORS.*`, hex) and palette paths (`primary.main`) working.
        root: ({ ownerState, theme }) => {
          const { color } = ownerState;
          if (typeof color !== 'string' || isTypographyPaletteColor(theme.palette, color)) return {};
          const fromPalette = color.includes('.') ? getPath(theme.palette, color) : undefined;
          return { color: typeof fromPalette === 'string' ? fromPalette : color };
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Lavender canvas under white cards and headers, as in customer-mobile
        body: {
          background: COLORS.background[3],
        },
        // Card lists fade in and slide up, staggered, like customer-mobile's FadeInUp
        '@keyframes directoFadeInUp': {
          from: { opacity: 0, transform: 'translateY(24px)' },
          to: { opacity: 1, transform: 'none' },
        },
        '@keyframes directoFadeInDown': {
          from: { opacity: 0, transform: 'translateY(-4px)' },
          to: { opacity: 1, transform: 'none' },
        },
        // Honors the OS "reduce motion" setting: animations and transitions jump straight to their end state
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiSkeleton: {
      // Placeholders carry no content; the surrounding role="status" region announces the loading state
      defaultProps: { 'aria-hidden': true },
      styleOverrides: {
        root: {
          backgroundColor: COLORS.background[1],
          '@media (prefers-reduced-motion: reduce)': { animation: 'none', '&::after': { animation: 'none' } },
        },
        rounded: { borderRadius: RADIUS.sm },
        text: { borderRadius: RADIUS.sm },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: AlertOwnerState }) => ({
          ...(ownerState.severity === 'success' && {
            backgroundColor: COLORS.chip.success.background,
            color: COLORS.chip.success.text,
          }),
          ...(ownerState.severity === 'info' && {
            backgroundColor: COLORS.chip.info.background,
            color: COLORS.chip.info.text,
          }),
          ...(ownerState.severity === 'warning' && {
            backgroundColor: COLORS.chip.warning.background,
            color: COLORS.chip.warning.text,
          }),
          ...(ownerState.severity === 'error' && {
            backgroundColor: COLORS.chip.error.background,
            color: COLORS.chip.error.text,
          }),
          '& .MuiAlert-icon': { color: 'inherit' },
          borderRadius: RADIUS.lg,
          fontFamily: 'inherit',
          alignItems: 'center',
          '& .MuiAlert-action': { padding: 0, marginLeft: '16px' },
        }),
      },
    },
    // Pill buttons with press feedback, as in customer-mobile
    MuiButton: {
      variants: [
        {
          // Secondary text-only action (mobile "transparent" button)
          props: { variant: 'inverted' },
          style: {
            textTransform: 'none',
            color: COLORS.text.brand,
            backgroundColor: 'transparent',
            // Never filled, even on hover/press: mobile's transparent button has no background
            '&:hover': { backgroundColor: 'transparent', opacity: 0.8 },
            '& .MuiTouchRipple-root': { display: 'none' },
          },
        },
        {
          // Inline action inside text, like mobile's actionSubtitle ("Cambiar número"): bold brand text, no pill
          props: { variant: 'link' },
          style: {
            minHeight: 0,
            padding: 0,
            minWidth: 0,
            width: 'fit-content',
            borderRadius: 4,
            color: COLORS.text.brand,
            backgroundColor: 'transparent',
            fontWeight: 700,
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            '&:active:not(.Mui-disabled)': { transform: 'none' },
            '& .MuiTouchRipple-root': { display: 'none' },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            borderColor: COLORS.brand.main,
            color: COLORS.text.brand,
            '&:hover': { backgroundColor: COLORS.surface.brandTint, borderColor: COLORS.brand.main },
          },
        },
      ],
      defaultProps: {
        disableElevation: true,
        color: 'primary',
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          minHeight: 52,
          paddingInline: 16,
          borderRadius: RADIUS.full,
          fontWeight: 500,
          textTransform: 'none',
          lineHeight: 1.125,
          transition: `transform ${MOTION.fast}ms ${MOTION.easeOut}, background-color ${MOTION.short}ms, border-color ${MOTION.short}ms, color ${MOTION.short}ms`,
          '&:active:not(.Mui-disabled)': { transform: `scale(${MOTION.pressScale})` },
          // The text, inverted and link variants hide the ripple, MUI's only focus cue; keyboard focus gets a ring instead
          '&.Mui-focusVisible': KEYBOARD_FOCUS_RING,
        },
        sizeSmall: { minHeight: 36, paddingInline: 12 },
        contained: {
          '&.Mui-disabled': {
            backgroundColor: COLORS.state.disabledSurface,
            color: COLORS.state.disabledText,
          },
        },
        outlined: {
          backgroundColor: COLORS.surface.raised,
          '&.Mui-disabled': { borderColor: COLORS.state.disabledSurface, color: COLORS.state.disabledText },
        },
        // Same transparent secondary action as the inverted variant: never filled, fades on hover
        text: {
          color: COLORS.text.brand,
          background: 'none',
          '&:hover': { backgroundColor: 'transparent', opacity: 0.8 },
          '& .MuiTouchRipple-root': { display: 'none' },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        // Fields enter staggered on mount, like customer-mobile form screens; tables, toolbars and opted-out fields stay still
        root: {
          ...STAGGERED_FADE_IN_UP,
          '.MuiTableCell-root &, .MuiToolbar-root &, .MuiInputAdornment-root &, &[data-no-entrance]': {
            animation: 'none',
          },
        },
      },
    },
    MuiFormControlLabel: {
      variants: [
        {
          props: { variant: 'error' },
          style: {
            borderColor: COLORS.state.error,
            '&:focus-within': {
              borderColor: COLORS.state.error,
            },
          },
        },
        {
          props: { variant: 'checkbox' },
          style: {
            alignItems: 'center',
            gap: 1,
            pr: 1,
            '&:has(.Mui-checked)': {
              borderColor: COLORS.brand.main,
              backgroundColor: COLORS.background[0],
            },
          },
        },
      ],
      defaultProps: {
        labelPlacement: 'end',
      },
      styleOverrides: {
        label: {
          lineHeight: 'normal',
          fontSize: '0.80rem',
          color: COLORS.text.main,
        },
        // Same border as text inputs: brand hairline at rest, brand color on hover and focus
        root: {
          border: `1px solid ${COLORS.border.brand}`,
          backgroundColor: COLORS.surface.raised,
          margin: '0 0 16px',
          borderRadius: 8,
          paddingRight: 12,
          '&:hover': {
            borderColor: COLORS.brand.light,
          },
          '&:focus-within': {
            borderColor: COLORS.brand.main,
          },
          '&:has(.Mui-checked)': {
            borderColor: COLORS.brand.main,
          },
          '& .MuiFormControlLabel-asterisk': {
            display: 'none',
          },
          '& .MuiStack-root': {
            display: 'flex',
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        checkedIcon: (
          <Zoom in>
            <svg
              style={{ margin: '2px' }}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="20" height="20" rx="10" fill={COLORS.brand.main} />
              <path
                d="M14.6668 6.5L8.25016 12.9167L5.3335 10"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Zoom>
        ),
      },
      styleOverrides: {
        root: {
          color: COLORS.text.light2,
          '&.Mui-checked ~ .MuiFormControlLabel-label': {
            color: COLORS.text.main,
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        checkedIcon: (
          <Zoom in>
            <svg
              style={{ margin: '2px' }}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="20" height="20" rx="6" fill={COLORS.brand.main} />
              <path
                d="M14.6663 6.5L8.24967 12.9167L5.33301 10"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Zoom>
        ),
      },
      styleOverrides: {
        root: {
          color: COLORS.text.light2,
          '&.Mui-checked ~ .MuiFormControlLabel-label': {
            color: COLORS.text.main,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: COLORS.text.label,
          fontFamily: onest.style.fontFamily,
          fontSize: '0.875rem',
          lineHeight: '1.125rem',
          fontWeight: 400,
          position: 'relative',
          transform: 'unset',
          marginBottom: '8px',
          '&.Mui-focused': {
            color: COLORS.text.label,
          },
          '& .MuiFormLabel-asterisk': { color: COLORS.state.error },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: COLORS.text.label,
          fontFamily: onest.style.fontFamily,
          fontSize: '0.875rem',
          lineHeight: '1.125rem',
          fontWeight: 400,
          position: 'relative',
          transform: 'unset',
          marginBottom: '8px',
          '&.Mui-focused': {
            color: COLORS.text.label,
          },
          '& .MuiFormLabel-asterisk': { color: COLORS.state.error },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        slotProps: {
          inputLabel: { shrink: true },
        },
      },
      styleOverrides: {
        root: {
          '& legend': {
            maxWidth: '0%',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '&[data-focus="true"] fieldset': {
            borderColor: COLORS.brand.main,
          },
        },
      },
    },
    // MuiFormHelperText: {
    //   styleOverrides: {
    //     root: {
    //       textWrap: 'nowrap',
    //     },
    //   },
    // },
    MuiSelect: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          fontSize: '1rem',
          '& legend': {
            maxWidth: '0%',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.surface.raised,
          // Border eases between rest, hover, focus and error instead of jumping
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.border.brand,
            transition: `border-color ${MOTION.fast}ms ${MOTION.easeOut}`,
          },
          '&:hover:not(.Mui-disabled, .Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.brand.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.brand.main,
            borderWidth: '1px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.state.error },
          // Disabled keeps the brand outline, as in customer-mobile; MUI's default greys it out
          '&.Mui-disabled': {
            backgroundColor: COLORS.surface.muted,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border.brand },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: onest.style.fontFamily,
          fontSize: '0.75rem',
          lineHeight: '1rem',
          marginInline: 0,
          // Error messages ease in under the field instead of popping
          '&.Mui-error': { animation: `directoFadeInDown ${MOTION.short}ms ${MOTION.easeOut} backwards` },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // Typed values in Cocogoose Light, as in customer-mobile; 16px keeps iOS from zooming on focus
        root: {
          color: COLORS.text.main,
          fontFamily: cocogoose.style.fontFamily,
          fontSize: '0.75rem',
          fontWeight: 300,
          borderRadius: `${RADIUS.md}px !important`,
          height: 44,
          '&.MuiInputBase-multiline': {
            height: 'unset',
          },
          input: {
            height: 'unset',
            padding: '10px 14px',
            lineHeight: 1,
            fontSize: '1rem',
            borderRadius: 'inherit',
            // Browser autofill paints its own blue/yellow background with !important: cover it with an inset shadow in the field color
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${COLORS.surface.raised} inset`,
              WebkitTextFillColor: COLORS.text.main,
              caretColor: COLORS.text.main,
            },
            '&:autofill': {
              boxShadow: `0 0 0 100px ${COLORS.surface.raised} inset`,
            },
            '&::placeholder': {
              color: COLORS.text.placeholder,
              opacity: 1,
              fontSize: '1rem',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          boxShadow: SHADOWS.floating,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          overflow: 'scroll',
          textAlign: 'center',
          gap: 24,
          padding: '32px 24px 24px',
          '&::-webkit-scrollbar': { display: 'none' },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 51, height: 31, padding: 0 },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: COLORS.surface.raised,
            '& + .MuiSwitch-track': { backgroundColor: COLORS.brand.main, opacity: 1 },
          },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
        },
        thumb: { width: 27, height: 27, boxShadow: 'none', color: COLORS.surface.raised },
        track: {
          borderRadius: 16,
          backgroundColor: COLORS.border.default,
          opacity: 1,
          transition: `background-color ${MOTION.short}ms`,
        },
      },
    },
    MuiSpeedDialAction: {
      styleOverrides: {
        staticTooltipLabel: {
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        // Brand color like the track: it marks controls, blue is kept for headings and amounts
        thumb: {
          color: COLORS.brand.main,
          '&.Mui-active': {
            boxShadow: `0 0 0 14px ${COLORS.brand.main}29 !important`,
          },
          '&.Mui-focusVisible, &:hover': {
            boxShadow: `0 0 0 8px ${COLORS.brand.main}29`,
          },
        },
        rail: {
          height: '8px',
        },
        track: {
          height: '8px',
        },
        mark: {
          height: '8px',
          width: '8px',
          borderRadius: '50%',
          transform: 'translate(-8px, -50%)',
          '&[data-index="0"]': {
            transform: 'translate(-0px, -50%)',
          },
        },
        // root: {
        //   '& .MuiSlider-mark': {
        //     height: '8px',
        //     width: '8px',
        //     borderRadius: '50%',
        //     '& .MuiSlider-mark:nth-last-child(1)': {
        //       color: 'red !important',
        //       transform: 'translate(-8px, -50%)',
        //     },
        //   },
        // },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-label': {
            '&.Mui-active': { color: COLORS.brand.main },
            '&.Mui-completed': { color: COLORS.brand.main },
          },
          '& .MuiStepLabel-labelContainer': {
            color: COLORS.state.disabled,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        list: {
          height: '100%',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: COLORS.text.light3,
          fontWeight: 500,
          '&.Mui-selected': { color: COLORS.text.brand },
          minWidth: 'unset',
          minHeight: 'unset',
          '@media (max-width: 900px) ': {
            fontSize: 0,
            '& .MuiTab-icon': {
              marginRight: '0px',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          height: '100%',
          aspectRatio: '1',
          '&.Mui-focusVisible': KEYBOARD_FOCUS_RING,
        },
      },
    },
    MuiTableRow: {
      variants: [
        {
          props: { variant: 'mobileFlex' },
          style: {
            // Rows enter staggered at every width, like customer-mobile lists
            ...STAGGERED_FADE_IN_UP,
            '@media (max-width: 900px) ': {
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              borderRadius: RADIUS.xl,
              boxShadow: SHADOWS.card,
              padding: '20px',
              backgroundColor: COLORS.surface.raised,
            },
            '& > td': {
              display: 'table-cell',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem',
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              height: '67px',
              flexWrap: 'wrap',
              '@media (max-width: 900px) ': {
                height: 'unset',
                display: 'flex',
                padding: 0,
                borderBottom: 'none',
              },
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          display: 'table-row',
          flexDirection: 'row',
          padding: '1rem',
          margin: '1rem',
          gap: 0,
          border: 'none',
          borderRadius: 0,
          backgroundColor: 'transparent',
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          display: 'flex',
          width: '100%',
          padding: '8px',
          alignItems: 'center',
          justifyContent: 'center',
          '& li': {
            '& .MuiPaginationItem-icon': {
              color: COLORS.brand.main,
            },
            '& .Mui-selected': {
              backgroundColor: COLORS.surface.brandTint,
              '&:hover': {
                backgroundColor: COLORS.background[1],
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.xxl,
          height: 'auto',
          padding: '4px 10px',
          gap: 6,
          fontFamily: onest.style.fontFamily,
          fontWeight: 700,
          fontSize: '0.875rem',
          lineHeight: '1.125rem',
        },
        label: { padding: 0 },
        icon: { margin: 0, color: 'inherit' },
        colorInfo: {
          color: COLORS.chip.info.text,
          backgroundColor: COLORS.chip.info.background,
        },
        colorWarning: {
          color: COLORS.chip.warning.text,
          backgroundColor: COLORS.chip.warning.background,
        },
        colorSuccess: {
          color: COLORS.chip.success.text,
          backgroundColor: COLORS.chip.success.background,
        },
        colorError: {
          color: COLORS.chip.error.text,
          backgroundColor: COLORS.chip.error.background,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      // Borderless white card lifted by a soft shadow, as in customer-mobile
      styleOverrides: {
        root: {
          border: 'none',
          borderRadius: RADIUS.xl,
          boxShadow: SHADOWS.card,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRadius: 0, boxShadow: 'none', borderRight: `1px solid ${COLORS.border.brand}` },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { borderRadius: 0, boxShadow: 'none' },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: RADIUS.lg, boxShadow: SHADOWS.floating },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: 'none',
          boxShadow: 'none',
          '& .MuiBox-root:nth-last-child(1)': {
            borderBottom: `none`,
          },
        },
      },
    },
  },
});
